import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IS24,
  ONBOARDING_QUERY_PARAM,
  ONBOARDING_QUERY_VALUE,
  resolveIS24BaseUrl,
  resolveListingState,
} from './mieterNetzwerk.utils';
import { tenantNetworkClient } from '@/tenantNetwork/client';
import { useGetTenantNetworkListing, useGetTenantNetworkStatus } from '@/apiHooks/tenantNetwork.hook';

export const useTenantNetworkController = (isVisible: boolean, onClose: () => void) => {
  const { initializing, tenantNetworkStatus } = useGetTenantNetworkStatus(isVisible);
  const propertyHubId = tenantNetworkStatus?.propertyHubId ?? null;

  const { tenantNetworkListing } = useGetTenantNetworkListing(
    propertyHubId,
    isVisible && tenantNetworkStatus !== undefined && !!propertyHubId,
  );

  const listingState = useMemo(
    () => resolveListingState(tenantNetworkStatus?.status, tenantNetworkListing?.status),
    [tenantNetworkStatus?.status, tenantNetworkListing?.status],
  );

  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isVisible) tenantNetworkClient.prefetch('onboarding').catch(() => undefined);
  }, [isVisible]);

  const clearOnboardingQueryParam = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(ONBOARDING_QUERY_PARAM);
    window.history.replaceState({}, '', url);
  }, []);

  const handleOnboardingClose = useCallback(() => {
    clearOnboardingQueryParam();
    setIsOnboardingActive(false);
    onClose();
  }, [clearOnboardingQueryParam, onClose]);

  const handleOpenMeinImmobilien = useCallback(() => {
    const path = propertyHubId ? `${IS24.PATHS.meinImmobilien}/${propertyHubId}` : IS24.PATHS.meinImmobilienOverview;
    window.open(`${resolveIS24BaseUrl()}${path}`, '_blank', 'noopener,noreferrer');
  }, [propertyHubId]);

  const handleOpenHomepage = useCallback(() => {
    window.open(`${resolveIS24BaseUrl()}${IS24.PATHS.homepage}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleStartOnboarding = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set(ONBOARDING_QUERY_PARAM, ONBOARDING_QUERY_VALUE);
    window.history.replaceState({}, '', url);
    setIsOnboardingActive(true);

    setIsWidgetLoading(true);
    try {
      await tenantNetworkClient.openOnboarding({
        type: 'PROPERTY_HUB',
        onClose: handleOnboardingClose,
        onComplete: handleOnboardingClose,
      });
    } catch {
      clearOnboardingQueryParam();
      if (isMountedRef.current) {
        setIsOnboardingActive(false);
      }
    } finally {
      if (isMountedRef.current) {
        setIsWidgetLoading(false);
      }
    }
  }, [clearOnboardingQueryParam, handleOnboardingClose]);

  return {
    initializing,
    listingState,
    isOnboardingActive,
    isWidgetLoading,
    handleOpenMeinImmobilien,
    handleOpenHomepage,
    handleStartOnboarding,
  };
};
